import React, { useRef, useState } from 'react';
import { FiCamera, FiScissors, FiRefreshCw } from 'react-icons/fi';
import './ImageUpload.css';

/**
 * ImageUpload Component
 * Handles image file selection and preview
 */
const ImageUpload = ({ onImageSelect, selectedImage, onCropClick }) => {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  /**
   * Handle file selection
   */
  const handleFileSelect = (file) => {
    if (!file) return;
    
    // Check file type
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file (JPG, PNG, WEBP, or GIF)');
      return;
    }
    
    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      alert('File size exceeds 10MB limit. Please choose a smaller image.');
      return;
    }
    
    onImageSelect(file);
  };

  /**
   * Handle file input change
   */
  const handleChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  /**
   * Handle drag and drop
   */
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      // Only set dragActive to false if we're leaving the drop zone
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX;
      const y = e.clientY;
      if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
        setDragActive(false);
      }
    }
  };

  /**
   * Handle drop
   */
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  /**
   * Trigger file input click
   */
  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="image-upload-container">
      <h2 className="section-title">Upload Your Photo</h2>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleChange}
        className="file-input"
        style={{ display: 'none' }}
      />

      {!selectedImage ? (
        <div
          className={`upload-area ${dragActive ? 'drag-active' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={handleClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClick();
            }
          }}
          aria-label="Upload image"
        >
          <div className="upload-icon">
            <FiCamera />
          </div>
          <p className="upload-text">
            Click to upload or drag and drop
          </p>
          <p className="upload-hint">
            Supports JPG, PNG, WEBP (Max 10MB)
          </p>
        </div>
      ) : (
        <div className="image-preview-container">
          <div className="preview-image-wrapper">
            <img
              src={URL.createObjectURL(selectedImage)}
              alt="Selected"
              className="preview-image"
              loading="lazy"
            />
            <div className="image-overlay-actions">
              <button
                className="action-btn crop-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onCropClick();
                }}
                title="Crop Image"
                type="button"
              >
                <FiScissors /> <span className="hidden sm:inline">Crop</span>
              </button>
              <button
                className="action-btn change-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClick();
                }}
                title="Change Image"
                type="button"
              >
                <FiRefreshCw /> <span className="hidden sm:inline">Change</span>
              </button>
            </div>
          </div>
          <div className="image-info">
            <p className="image-name" title={selectedImage.name}>{selectedImage.name}</p>
            <p className="image-size">{(selectedImage.size / 1024 / 1024).toFixed(2)} MB</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;



